import { DEFAULT_PUBLIC_APP_SETTINGS, type PublicAppSettings } from '../config/appPublicSettings';
import { getSupabaseClient } from '../../services/supabase/client';
import { publicGetAppSettings } from './publicAppSettingsService';
import { logError } from '../utils/logger';

/** 通知切断時にも設定を再照合する間隔。 */
const FALLBACK_INTERVAL_MS = 30_000;
/** 通知をまとめ、公開チャンネルへの連続送信によるRPC増加を抑える間隔。 */
const NOTIFICATION_DELAY_MS = 500;
/** 全画面で共有する公開設定。hasLoadedは一度でも取得に成功し、settingsが確定値であることを表す。 */
let snapshot: { settings: PublicAppSettings; isLoading: boolean; hasError: boolean; hasLoaded: boolean } = {
  settings: DEFAULT_PUBLIC_APP_SETTINGS,
  isLoading: process.env.NODE_ENV !== 'test',
  hasError: false,
  hasLoaded: false,
};
/** Reactの購読先。 */
const listeners = new Set<() => void>();
/** 最後の購読解除時の停止処理。 */
let stopSync: (() => void) | undefined;
/** StrictModeや画面遷移の即時再購読では同じ接続を維持する。 */
let stopTimer: ReturnType<typeof setTimeout> | undefined;

/**
 * 再取得した設定のうち、内容が変わらない項目は前回の値をそのまま使う。
 * 取得のたびに配列・objectが作り直されると、それを依存に持つ画面の取得effectが内容の変化なしに再実行されるため。
 * @param previous - 前回の設定
 * @param next - 今回取得した設定
 * @returns 全項目が同じなら前回の設定そのもの、そうでなければ変わった項目だけ差し替えた設定
 */
const reuseUnchangedSettings = (previous: PublicAppSettings, next: PublicAppSettings): PublicAppSettings => {
  const merged = Object.fromEntries(Object.entries(next).map(([key, value]) => {
    const previousValue = previous[key as keyof PublicAppSettings];
    return [key, JSON.stringify(previousValue) === JSON.stringify(value) ? previousValue : value];
  })) as PublicAppSettings;
  const isUnchanged = Object.keys(merged).length === Object.keys(previous).length
    && Object.entries(merged).every(([key, value]) => value === previous[key as keyof PublicAppSettings]);
  return isUnchanged ? previous : merged;
};

/** 公開設定の同一参照スナップショットを返す。 */
export const getPublicAppSettingsSnapshot = () => snapshot;

/**
 * 一つの購読接続で全画面の設定を同期する。通知payloadは信用せずRPCの確定値を使う。
 * @returns 通信とタイマーの停止処理
 */
export const startPublicAppSettingsSync = (): (() => void) => {
  /** この接続の生存状態。 */
  let isActive = true;
  /** 設定RPCの重複実行を抑止する。 */
  let isFetching = false;
  /** RPC待機中の通知を保持する。 */
  let shouldRefetch = false;
  /** Realtimeの接続状態。 */
  let isSubscribed = false;
  /** 通知の集約タイマー。 */
  let notificationTimer: ReturnType<typeof setTimeout> | undefined;
  /** 公開設定取得と通知購読用クライアント。 */
  const supabase = getSupabaseClient();

  /** 通信中の更新通知を保留し、古い応答の後にも必ず再取得する。 */
  const refresh = async (): Promise<void> => {
    if (!isActive) return;
    if (isFetching) { shouldRefetch = true; return; }
    isFetching = true;
    try {
      const settings = await publicGetAppSettings();
      if (isActive) {
        snapshot = {
          settings: reuseUnchangedSettings(snapshot.settings, settings),
          isLoading: false,
          hasError: false,
          hasLoaded: true,
        };
      }
    } catch {
      if (isActive) snapshot = { ...snapshot, isLoading: false, hasError: true };
      logError('公開設定を再取得できませんでした。最後に取得した設定を保持します。');
    } finally {
      isFetching = false;
      if (isActive) listeners.forEach((listener) => listener());
      if (shouldRefetch && isActive) {
        shouldRefetch = false;
        scheduleRefresh();
      }
    }
  };
  /** 複数通知をまとめる。payloadから設定値は取り込まない。 */
  const scheduleRefresh = (): void => {
    if (notificationTimer || !isActive) return;
    notificationTimer = setTimeout(() => {
      notificationTimer = undefined;
      void refresh();
    }, NOTIFICATION_DELAY_MS);
  };
  /** 画面復帰・回線復帰時に取りこぼしを回収する。 */
  const refreshVisible = (): void => {
    if (document.visibilityState !== 'hidden') void refresh();
  };
  /** 設定値を受け取らない公開通知チャンネル。 */
  const channel = supabase.channel('visitor-app-settings', { config: { private: false } })
    .on('broadcast', { event: 'settings_changed' }, scheduleRefresh)
    .subscribe((status) => {
      isSubscribed = status === 'SUBSCRIBED';
      if (isSubscribed) refreshVisible();
    });
  void refresh();
  /** 通知接続がないときの代替再照合。 */
  const interval = setInterval(() => {
    if (!isSubscribed || snapshot.hasError) refreshVisible();
  }, FALLBACK_INTERVAL_MS);
  window.addEventListener('focus', refreshVisible);
  window.addEventListener('online', refreshVisible);
  document.addEventListener('visibilitychange', refreshVisible);
  return () => {
    isActive = false;
    clearInterval(interval);
    clearTimeout(notificationTimer);
    window.removeEventListener('focus', refreshVisible);
    window.removeEventListener('online', refreshVisible);
    document.removeEventListener('visibilitychange', refreshVisible);
    void supabase.removeChannel(channel).catch(() => {
      logError('公開設定の更新通知を解除できませんでした。');
    });
  };
};

/**
 * 画面から公開設定を購読する。既存UIテストの通信抑止は維持する。
 * @param listener Reactの更新通知
 * @returns 購読解除
 */
export const subscribePublicAppSettings = (listener: () => void): (() => void) => {
  clearTimeout(stopTimer);
  listeners.add(listener);
  if (!stopSync && process.env.NODE_ENV !== 'test') stopSync = startPublicAppSettingsSync();
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      stopTimer = setTimeout(() => { stopSync?.(); stopSync = undefined; }, 0);
    }
  };
};
